import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import {
  Modal,
  PrimaryButton,
  CheckboxInput,
  RadioInput,
  LoadingIndicator,
} from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import { theme } from 'lib/style';
import { getNotificationPreference, saveNotificationPreference } from 'lib/api';
import { useAuth, useGlobalState } from '../../../../lib/context';
import { soundType } from 'lib/const/notifications';
import { useToastError } from 'lib/hooks';
import { successToast } from 'lib/components/toasts/success';
import {
  getPreferencesOptions,
  NOTIFICATION_EMAILS,
  NOTIFICATION_IN_APP,
} from '../const';
import { checkIfFreemium } from 'lib/utils/functions';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};

  font-style: normal;
  font-size: 18px;
  line-height: 24px;
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  margin-top: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  gap: 100px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  flex: 1;
`;

const PreferenceName = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #272a32;
  width: 300px;
`;

const Footer = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
`;

const NotifyMeContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding-top: 12px;
  padding-bottom: 40px;
  .single--block {
    display: flex;
    column-gap: 15px;
    align-items: center;
    border-bottom: 1px solid #00000014;
    padding-bottom: 8px;
    gap: 42px;
  }
`;

const SoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding-top: 12px;
  .single--block {
    display: flex;
    column-gap: 10px;
    align-items: center;
  }
`;

const ChecboxTitles = styled.div`
  display: flex;
  flex-direction: row;
  gap: 25px;
  margin-left: 330px;
  width: 50%;

  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #272a32;
`;

type Props = {
  handleModalClose: (shouldRefresh?: boolean) => void;
};

const PreferenceSounds = [
  {
    name: 'Mute',
    value: 'Mute',
  },
  {
    name: 'Pop Ding',
    value: 'Pop Ding',
  },
  {
    name: 'Bongo Drum',
    value: 'Bongo Drum',
  },
  {
    name: 'Jingle Bells',
    value: 'Jingle Bells',
  },
  {
    name: 'Ding Dong',
    value: 'Ding Dong',
  },
  {
    name: 'Flute',
    value: 'Flute',
  },
  {
    name: 'Piano',
    value: 'Piano',
  },
];

export const PreferenceModal = (props: Props) => {
  const { handleModalClose } = props;

  const [loading, setLoading] = React.useState(true);
  const [selectedNotify, setSelectedNotify] = React.useState<any>([]);
  const [selectedSound, setSelectedSound] = React.useState<any>('');
  const [preferenceId, setPreferenceId] = React.useState<any>('');
  const [preferenceOptions, setPreferenceOptions] = React.useState<
    Array<{ [key: string]: string | number | boolean | undefined }>
  >([]);
  const { ref } = useOnClickOutside(handleModalClose);
  const themes = useTheme();
  const { userData } = useAuth();
  const isFreemium = checkIfFreemium(userData);

  const {
    state: { whitelabel },
  } = useGlobalState();

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  const { showError } = useToastError();

  const fetchPreferences = async () => {
    try {
      const preferenceResponse = await getNotificationPreference();
      const ignoreKey = ['preferenceId', 'sound', 'createdAt', 'userId'];
      const selectedNotifyTemp: any = [];
      Object.keys(preferenceResponse).forEach((pref: any) => {
        if (preferenceResponse[pref] && !ignoreKey.includes(pref)) {
          selectedNotifyTemp.push(pref);
        }
      });
      setSelectedNotify(selectedNotifyTemp);
      setSelectedSound(preferenceResponse.sound);
      setPreferenceId(
        preferenceResponse.preferenceId !== null
          ? preferenceResponse.preferenceId
          : ''
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const updatePreference = async () => {
    try {
      const payload: any = {
        sound: selectedSound,
      };
      selectedNotify.forEach((notification: string) => {
        if (notification === 'updatedAt') {
          payload[notification] = new Date();
          return;
        }
        payload[notification] = selectedNotify.includes(notification);
      });
      await saveNotificationPreference(preferenceId, payload);
      handleModalClose();
      successToast({ title: 'Preference saved!' });
    } catch (error) {
      setLoading(false);
      showError(error);
    }
  };

  React.useEffect(() => {
    if (!userData) {
      return;
    }

    let preferenceOptions = getPreferencesOptions(userData);
    if (userData.isAutomotiveServiceRole) {
      const quoteOptionObject = {
        id: 7,
        name: 'Quote Updates',
        valueEmail: NOTIFICATION_EMAILS.QUOTE_UPDATE,
        valueInApp: NOTIFICATION_IN_APP.QUOTE_UPDATE,
      };
      // to cover edge case - opening modal on the same load
      !preferenceOptions.some(option => option.id === quoteOptionObject.id) &&
        preferenceOptions.push(quoteOptionObject);
    }
    // add sales preferences
    else if (userData.isAutomotiveSalesRole) {
      const testDriveObject = {
        id: 8,
        name: 'Test Drive Schedule',
        valueEmail: NOTIFICATION_EMAILS.TEST_DRIVE,
        valueInApp: NOTIFICATION_IN_APP.TEST_DRIVE,
      };
      // to cover edge case - opening modal on the same load
      !preferenceOptions.some(option => option.id === testDriveObject.id) &&
        preferenceOptions.push(testDriveObject);
    }
    if (userData.isVdpAllowed) {
      const videoPushedToVdpObject = {
        id: 11,
        name: 'Video Pushed to VDP',
        valueEmail: NOTIFICATION_EMAILS.VDP,
        valueInApp: NOTIFICATION_IN_APP.VDP,
      };
      // to cover edge case - opening modal on the same load
      !preferenceOptions.some(
        option => option.id === videoPushedToVdpObject.id
      ) && preferenceOptions.push(videoPushedToVdpObject);
    }

    if (whitelabel.name !== 'Covideo') {
      preferenceOptions = preferenceOptions.filter(
        option => option.id !== 5 && option.id !== 6
      );
    }

    setPreferenceOptions(preferenceOptions);
  }, [userData.automotiveRole, whitelabel]);

  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  React.useEffect(() => {
    fetchPreferences();
  }, []);

  return (
    <Modal
      style={{ position: 'fixed' }}
      widthOnDesktop={'800px'}
      overrideBodyHeight={true}
      maxHeight={
        isFreemium
          ? '510px'
          : userData.isAutomotiveServiceRole || userData.isAutomotiveSalesRole
          ? '650px'
          : '600px'
      }
    >
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>Notifications Preferences</HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose()}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        {!loading ? (
          <Content>
            <Column>
              <ChecboxTitles>
                <span>Email</span>
                {/*<span>In App</span>*/}
              </ChecboxTitles>
              <NotifyMeContainer>
                {preferenceOptions.map((ele: any) => (
                  <div className='single--block' key={ele.id}>
                    <PreferenceName>{ele.name}</PreferenceName>
                    <CheckboxInput
                      checked={selectedNotify.includes(ele.valueEmail)}
                      onChange={(e: React.SyntheticEvent) => {
                        let { checked } = e.target as HTMLInputElement;
                        if (checked) {
                          setSelectedNotify([
                            ...selectedNotify,
                            ele.valueEmail,
                          ]);
                        } else {
                          setSelectedNotify(
                            selectedNotify.filter(
                              (e: any) => e != ele.valueEmail
                            )
                          );
                        }
                      }}
                    />

                    {/*<CheckboxInput*/}
                    {/*  checked={selectedNotify.includes(ele.valueInApp)}*/}
                    {/*  onChange={(e: React.SyntheticEvent) => {*/}
                    {/*    let { checked } = e.target as HTMLInputElement;*/}
                    {/*    if (checked) {*/}
                    {/*      setSelectedNotify([*/}
                    {/*        ...selectedNotify,*/}
                    {/*        ele.valueInApp,*/}
                    {/*      ]);*/}
                    {/*    } else {*/}
                    {/*      setSelectedNotify(*/}
                    {/*        selectedNotify.filter(*/}
                    {/*          (e: any) => e != ele.valueInApp*/}
                    {/*        )*/}
                    {/*      );*/}
                    {/*    }*/}
                    {/*  }}*/}
                    {/*/>*/}
                  </div>
                ))}
              </NotifyMeContainer>
            </Column>
            <Column>
              Sound
              <SoundContainer>
                {PreferenceSounds.map((ele: any) => (
                  <div className='single--block' key={ele.name}>
                    <RadioInput
                      name={'textAndIconColor'}
                      value={ele.value}
                      text={ele.name}
                      checkedColor={themes.colors.primaryColor}
                      checked={selectedSound == ele.value}
                      onClick={() => {
                        setSelectedSound(ele.value);
                        const sound = ele.value
                          .toLowerCase()
                          .replaceAll(' ', '');
                        if (soundType[sound])
                          new Audio(soundType[sound]).play();
                      }}
                      onChange={() => {
                        setSelectedSound(ele.value);
                      }}
                    />
                  </div>
                ))}
              </SoundContainer>
            </Column>
          </Content>
        ) : (
          <Content>
            <LoadingIndicator isLoading={loading} />
          </Content>
        )}
        <Footer>
          <PrimaryButton
            text='Save Changes'
            textColor='white'
            borderRadius={5}
            fontSize='15px'
            textPadding='8px 16px'
            onClick={updatePreference}
            disabled={loading}
          />
        </Footer>
      </div>
    </Modal>
  );
};
